import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ChecklistItem } from '../../types';
import { apiService } from '../../services/api';

interface ChecklistsState {
  items: ChecklistItem[];
  loading: boolean;
  error: string | null;
}

const initialState: ChecklistsState = {
  items: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchChecklistItems = createAsyncThunk(
  'checklists/fetchItems',
  async ({ routeId, type }: { routeId: number; type?: string }) => {
    return await apiService.getChecklistItems(routeId, type);
  }
);

export const createChecklistItem = createAsyncThunk(
  'checklists/createItem',
  async ({ routeId, data }: { routeId: number; data: Partial<ChecklistItem> }) => {
    return await apiService.createChecklistItem(routeId, data);
  }
);

export const updateChecklistItem = createAsyncThunk(
  'checklists/updateItem',
  async ({ id, data }: { id: number; data: Partial<ChecklistItem> }) => {
    return await apiService.updateChecklistItem(id, data);
  }
);

export const deleteChecklistItem = createAsyncThunk(
  'checklists/deleteItem',
  async (id: number) => {
    await apiService.deleteChecklistItem(id);
    return id;
  }
);

export const toggleChecklistItem = createAsyncThunk(
  'checklists/toggleItem',
  async (id: number) => {
    return await apiService.toggleChecklistItem(id);
  }
);

export const reorderChecklistItems = createAsyncThunk(
  'checklists/reorderItems',
  async ({ routeId, itemOrders }: { routeId: number; itemOrders: Array<{ item_id: number; order: number }> }) => {
    return await apiService.reorderChecklistItems(routeId, itemOrders);
  }
);

export const moveToEvents = createAsyncThunk(
  'checklists/moveToEvents',
  async ({ id, dayId }: { id: number; dayId: number }) => {
    return await apiService.moveToEvents(id, dayId);
  }
);

const checklistsSlice = createSlice({
  name: 'checklists',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch items
      .addCase(fetchChecklistItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchChecklistItems.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchChecklistItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Ошибка загрузки списков';
      })
      // Create item
      .addCase(createChecklistItem.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      // Update item
      .addCase(updateChecklistItem.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // Delete item
      .addCase(deleteChecklistItem.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
      })
      // Toggle item
      .addCase(toggleChecklistItem.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // Reorder items
      .addCase(reorderChecklistItems.fulfilled, (state, action) => {
        state.items = action.payload;
      })
      // Move to events
      .addCase(moveToEvents.fulfilled, (state, action) => {
        // Удаляем элемент из списка, так как он перемещен в события
        state.items = state.items.filter(item => item.id !== action.payload.id);
      });
  },
});

export const { clearError } = checklistsSlice.actions;
export default checklistsSlice.reducer; 